import React from 'react';
import { gql, useMutation, useQuery } from "@apollo/client";
import { Badge, Button, Drawer, Image } from "@blend-ed/blendx-ui";
import { getConfig } from "@edx/frontend-platform";
import { injectIntl, intlShape } from '@edx/frontend-platform/i18n';
import PropTypes from "prop-types";
import { useCurrency } from "../../../hooks/useCurrency";
import { getFeatureToggle } from "../../../utils/getFeatureToggle";
import placeholderDark from '../../../assets/images/placeholder-dark.png';
import placeholder from '../../../assets/images/placeholder.jpg';
import messages from './messages';

const PAYMENT_GATEWAY_STATUS = gql`
  query GetOrganisationPaymentGatewayStatus {
    organisationPaymentGatewayStatus {
      paymentGatewayEnabled
      organisationId
    }
  }
`;

const DELETE_CART_ITEM = gql`
  mutation DeleteCartItem($cartId: Uuid!) {
    v2DeleteCartById(keyId: $cartId) {
      affectedRows
    }
  }
`;

const ADD_TO_WISHLIST = gql`
  mutation AddToWishlist($cartId: Uuid!) {
    v2UpdateCartById(
      keyId: $cartId
      postCheck: {}
      updateColumns: { status: { set: "wishlist" } }
    ) {
      affectedRows
    }
  }
`;

const CartItem = ({ intl, image, name, description, price, key, type, id, refetch, LMS_BASE_URL }) => {
  const { symbol } = useCurrency();
  const [deleteCartItem] = useMutation(DELETE_CART_ITEM,
    { context: { headers: { "x-hasura-role": "learner" } } }
  );
  const [addToWishlist] = useMutation(ADD_TO_WISHLIST,
    { context: { headers: { "x-hasura-role": "learner" } } }
  );

  const handleDeleteCartItem = () => {
    deleteCartItem({ variables: { cartId: id }, onCompleted: () => refetch() });
  };

  const handleAddToWishlist = () => {
    addToWishlist({ variables: { cartId: id }, onCompleted: () => refetch() });
  };

  const isDarkMode = document.documentElement.getAttribute('data-theme') === 'dark';

  return (
    <div className="cart-item" key={key}>
      <div className="cart-item__content">
        <div className="cart-item__content__image-wrapper">
          <Image src={`${LMS_BASE_URL}${image}`} alt={name} className="cart-item__content__image" onError={(e) => {
            e.target.src = isDarkMode ? placeholderDark : placeholder;
          }} />
        </div>
        <div className="cart-item__content__info">
          <div className="cart-item__content__info__details">
            <div className="cart-item__content__info__badge">
              <Badge variant={type === "course" ? "yellow" : "primary"}>{type === "course" ? intl.formatMessage(messages.courseBadge) : intl.formatMessage(messages.programBadge)}</Badge>
            </div>
            <div className="cart-item__content__info__name">{name}</div>
            <div className="cart-item__content__info__description">{description}</div>
          </div>
          <div className="cart-item__content__info__price">{`${symbol} ${price}`}</div>
        </div>
      </div>

      <div className="cart-item__actions">
        <Button size="xs" variant="outline-gray" onClick={handleDeleteCartItem}>{intl.formatMessage(messages.removeButton)}</Button>
        <Button size="xs" variant="outline-gray" onClick={handleAddToWishlist}>{intl.formatMessage(messages.moveToWishlistButton)}</Button>
      </div>
    </div>
  )
}

CartItem.propTypes = {
  intl: intlShape.isRequired,
};

const CartItemWithIntl = injectIntl(CartItem);

const CartDrawer = ({ intl, show, onClose, cartData, cartRefetch }) => {
  const { LMS_BASE_URL } = getConfig();
  const { symbol } = useCurrency();
  const { features_payments } = getFeatureToggle();
  const { data } = useQuery(PAYMENT_GATEWAY_STATUS, {
    context: {
      headers: {
        "x-hasura-role": "learner"
      }
    }
  });

  const paymentGatewayEnabled = data?.organisationPaymentGatewayStatus[0]?.paymentGatewayEnabled && features_payments;

  // Check if cart has any paid courses
  const hasPaidCourses = cartData.some(item => item.price > 0);

  // Check if cart has only free courses
  const hasOnlyFreeCourses = cartData.some(item => item.type === "course" || item.type === "program" && item.price === 0) && !hasPaidCourses;

  // Determine button text based on cart contents
  const getButtonText = () => {
    if (hasOnlyFreeCourses) {
      return intl.formatMessage(messages.enrollFreeButton);
    }
    return intl.formatMessage(messages.checkoutButton);
  };

  return (
    <Drawer show={show} onClose={onClose} className="cart-drawer">
      <Drawer.Header title={`${intl.formatMessage(messages.cartTitle)} (${cartData.length})`} />
      <Drawer.Body className="cart-drawer__body">
        {cartData.map((item) =>
          <CartItemWithIntl refetch={cartRefetch} key={item.id} {...item} LMS_BASE_URL={LMS_BASE_URL} />
        )}
      </Drawer.Body>
      <Drawer.Footer className="cart-drawer__footer">
        <div className="cart-drawer__footer__total">
          <div className="cart-drawer__footer__total__label">{intl.formatMessage(messages.subtotalLabel)}</div>
          <div className="cart-drawer__footer__total__price">{`${symbol} ${cartData.reduce((acc, item) => acc + (item.price || 0), 0)}`}</div>
        </div>
        <div className="cart-drawer__footer__actions">
          <Button variant="outline-gray" onClick={onClose}>{intl.formatMessage(messages.cancelButton)}</Button>

          {/* Show checkout/enroll button if there are items in cart */}
          {cartData.length > 0 && (paymentGatewayEnabled || hasOnlyFreeCourses) && (
            <Button
              variant="primary"
              href={`${LMS_BASE_URL}/blendxapi/payment/stripe/create_payment_intent/`}
            >
              {getButtonText()}
            </Button>
          )}

          {/* Show message if payment gateway is disabled and there are paid courses */}
          {!paymentGatewayEnabled && hasPaidCourses && (
            <Button variant="outline-gray" disabled>{intl.formatMessage(messages.paymentGatewayDisabled)}</Button>
          )}
        </div>
      </Drawer.Footer>
    </Drawer>
  );
};

CartDrawer.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func,
  cartData: PropTypes.array,
  intl: intlShape.isRequired,
};

CartDrawer.defaultProps = {
  show: false,
  onClose: () => { },
  cartData: []
};

export default injectIntl(CartDrawer);
